// The persistent editor-first shell (C0-R). A React-Router layout route: it stays
// MOUNTED across model/view switches (the sidebar, ModelsProvider and top bar do
// not remount) while the <Outlet/> swaps between /editor/:id, /browse, /settings.
// Per the C2 one-doc-per-model rule the editor canvas + its sync session DO tear
// down/recreate per model — that happens inside EditorPage, not here.
//
// Layout:  [ collapsible ModelSidebar | [ glass top bar ] + <Outlet/> ]
// The top bar's centre is a PORTAL SLOT the active page fills (EditorPage injects
// the model name + rename + sync status); the shell owns the global controls
// (theme quick-toggle, user menu). Main-bundle only — no cad-react/konva here.
import { useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { usePreferences } from '../preferences/PreferencesContext';
import type { ThemeMode } from '../api/types';
import { ModelsProvider, useModels } from '../models/ModelsContext';
import ModelSidebar from '../components/ModelSidebar';
import UpgradePrompt from '../components/UpgradePrompt';
import { Toaster } from '../components/ui/sonner';
import { TooltipProvider } from '../components/ui/tooltip';
import { TopBarSlotContext } from './topbar';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  CheckIcon,
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  PaletteIcon,
  SettingsIcon,
  SunIcon,
  UserIcon,
} from '../components/icons';

// ── Global chrome controls ───────────────────────────────────────────────────

const THEME_OPTIONS: { mode: ThemeMode; label: string; Icon: typeof SunIcon }[] = [
  { mode: 'system', label: 'System', Icon: MonitorIcon },
  { mode: 'light', label: 'Light', Icon: SunIcon },
  { mode: 'dark', label: 'Dark', Icon: MoonIcon },
];

function ThemeToggle() {
  const { prefs, update } = usePreferences();
  const mode = prefs.theme.mode;
  const Trigger =
    mode === 'custom' ? PaletteIcon : (THEME_OPTIONS.find((o) => o.mode === mode)?.Icon ?? MonitorIcon);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Theme" title="Theme">
          <Trigger />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        {THEME_OPTIONS.map(({ mode: m, label, Icon }) => (
          <DropdownMenuItem
            key={m}
            onSelect={() => update({ ...prefs, theme: { ...prefs.theme, mode: m } })}
          >
            <Icon />
            <span className="flex-1">{label}</span>
            {mode === m ? <CheckIcon /> : null}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings">
            <PaletteIcon />
            <span className="flex-1">Custom…</span>
            {mode === 'custom' ? <CheckIcon /> : null}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const initial = (me?.name?.trim()?.[0] || me?.email?.[0] || 'U').toUpperCase();
  async function onLogout() {
    await logout();
    navigate('/login', { replace: true });
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-primary text-xs font-semibold text-primary-foreground outline-none ring-offset-background transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="Account menu"
        >
          {me?.avatar_url ? (
            <img src={me.avatar_url} alt="" className="h-full w-full object-cover" />
          ) : (
            initial
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem]">
        <DropdownMenuLabel className="truncate normal-case text-foreground">
          {me?.email}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings">
            <SettingsIcon />
            Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/browse">
            <UserIcon />
            Browse all models
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={() => void onLogout()}>
          <LogOutIcon />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── The shell (inside ModelsProvider) ────────────────────────────────────────

function Shell() {
  const { prefs, update } = usePreferences();
  // Defensive: a stale/rolling server can omit `ui` — never crash the shell on it.
  const collapsed = prefs.ui?.modelSidebarCollapsed ?? false;
  const { limit, swapBusy, dismissLimit, swapArchive, active } = useModels();
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  const setCollapsed = (next: boolean) =>
    update({ ...prefs, ui: { ...(prefs.ui ?? {}), modelSidebarCollapsed: next } });

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex h-screen overflow-hidden bg-background text-foreground">
        <ModelSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Glass top bar: translucent panel + blur over the canvas/content.

              TWO ROWS now, and auto-height rather than `h-12`: the editor puts a
              menu bar on the first row and the object-properties strip + the
              GIS/Comments/Share cluster on the second, which no 48px row could
              hold (measured: the props strip rendered at y=-2, spilling above the
              header, while clipping its own dropdowns at 572px of content in a
              563px box). The page that owns the slot decides how many rows it
              fills; a page with one row's worth of content still renders one row.

              `relative z-50` is LOAD-BEARING, not decoration. This header is a
              STATIC sibling that precedes <main> in the DOM, so without its own
              stacking context every descendant — including an opened menu
              dropdown — loses the paint order to the Konva canvas inside <main>.
              That is not hypothetical: the Object Properties bar shipped this way
              and was invisible under the canvas, hit-testing to <canvas> at its
              own centre. A dropdown must overlay the drawing, so the context is
              created here, once, rather than with a z-index on each popup. */}
          <header className="relative z-50 flex shrink-0 flex-col gap-1 border-b border-border bg-[color-mix(in_srgb,var(--cad-panel-bg)_72%,transparent)] px-3 py-1 backdrop-blur-md">
            <div className="flex items-center gap-2">
              <div ref={setSlot} className="flex min-w-0 flex-1 items-center gap-2" />
              <ThemeToggle />
              <UserMenu />
            </div>
          </header>
          <main className="relative min-h-0 flex-1 overflow-hidden">
            <TopBarSlotContext.Provider value={slot}>
              <Outlet />
            </TopBarSlotContext.Provider>
          </main>
        </div>
      </div>

      {/* The freemium wall surfaces here so "+ New" from the SIDEBAR shows the same
          upgrade + archive-swap flow as /browse — the strongest conversion spot. */}
      {limit ? (
        <UpgradePrompt
          payload={limit}
          onClose={dismissLimit}
          archiveSwap={{
            models: active,
            busy: swapBusy,
            actionLabel: 'Archive & continue',
            onArchive: swapArchive,
          }}
        />
      ) : null}

      <Toaster />
    </TooltipProvider>
  );
}

export default function AppShell() {
  return (
    <ModelsProvider>
      <Shell />
    </ModelsProvider>
  );
}
